import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	CardDescription,
} from '@/components/ui/card'
import { CodeBlock } from '../CodeBlock'
import { SectionHeader } from '../SectionHeader'
import { useTranslation } from '@/hooks/useTranslation'

export const ProgrammaticTriggersSection = () => {
	const { t } = useTranslation()
	
	return (
		<section id="programmatic-triggers" className="mb-16">
			<SectionHeader id="programmatic-triggers">
				{t('docs.programmaticTriggers.title')}
			</SectionHeader>

			<Card className="mb-8 shadow-sm dark:bg-gray-800/50">
				<CardHeader>
					<CardTitle className="text-xl font-semibold">
						{t('docs.programmaticTriggers.manualControl.title')}
					</CardTitle>
					<CardDescription className="dark:text-gray-400">
						{t('docs.programmaticTriggers.manualControl.description')}
					</CardDescription>
				</CardHeader>
				<CardContent className="text-gray-700 dark:text-gray-300 space-y-4">
					<p>
						{t('docs.programmaticTriggers.manualControl.intro')}
					</p>

					<div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
						<h4 className="font-semibold text-blue-800 dark:text-blue-200 mb-2">
							📋 {t('docs.programmaticTriggers.commonUseCases.title')}
						</h4>
						<ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
							<li>
								• <strong>{t('docs.programmaticTriggers.commonUseCases.item1.label')}</strong>{' '}
								{t('docs.programmaticTriggers.commonUseCases.item1.text')}
							</li>
							<li>
								• <strong>{t('docs.programmaticTriggers.commonUseCases.item2.label')}</strong>{' '}
								{t('docs.programmaticTriggers.commonUseCases.item2.text')}
							</li>
							<li>
								• <strong>{t('docs.programmaticTriggers.commonUseCases.item3.label')}</strong>{' '}
								{t('docs.programmaticTriggers.commonUseCases.item3.text')}
							</li>
							<li>
								• <strong>{t('docs.programmaticTriggers.commonUseCases.item4.label')}</strong>{' '}
								{t('docs.programmaticTriggers.commonUseCases.item4.text')}
							</li>
							<li>
								• <strong>{t('docs.programmaticTriggers.commonUseCases.item5.label')}</strong>{' '}
								{t('docs.programmaticTriggers.commonUseCases.item5.text')}
							</li>
							<li>
								• <strong>{t('docs.programmaticTriggers.commonUseCases.item6.label')}</strong>{' '}
								{t('docs.programmaticTriggers.commonUseCases.item6.text')}
							</li>
							<li>
								• <strong>{t('docs.programmaticTriggers.commonUseCases.item7.label')}</strong>{' '}
								{t('docs.programmaticTriggers.commonUseCases.item7.text')}
							</li>
						</ul>
					</div>
				</CardContent>
			</Card>

			<Card className="mb-8 shadow-sm dark:bg-gray-800/50">
				<CardHeader>
					<CardTitle className="text-xl font-semibold flex items-center gap-2">
						<span className="flex items-center justify-center w-6 h-6 bg-primary/10 text-primary rounded-full text-sm font-bold">
							1
						</span>
						{t('docs.programmaticTriggers.step1.title')}
					</CardTitle>
				</CardHeader>
				<CardContent className="text-gray-700 dark:text-gray-300 space-y-3">
					<p>
						{t('docs.programmaticTriggers.step1.instruction')}
					</p>
					<ol className="list-decimal pl-5 space-y-2 text-sm">
						<li>
							{t('docs.programmaticTriggers.step1.listItem1')}
						</li>
						<li>{t('docs.programmaticTriggers.step1.listItem2')}</li>
						<li>
							{t('docs.programmaticTriggers.step1.listItem3')}
						</li>
						<li>{t('docs.programmaticTriggers.step1.listItem4')}</li>
					</ol>
					<div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-md border border-yellow-200 dark:border-yellow-700">
						<p className="text-sm text-yellow-800 dark:text-yellow-200">
							<strong>{t('docs.programmaticTriggers.step1.note.label')}</strong>{' '}
							{t('docs.programmaticTriggers.step1.note.text')}
						</p>
					</div>
				</CardContent>
			</Card>

			<Card className="mb-8 shadow-sm dark:bg-gray-800/50">
				<CardHeader>
					<CardTitle className="text-xl font-semibold flex items-center gap-2">
						<span className="flex items-center justify-center w-6 h-6 bg-primary/10 text-primary rounded-full text-sm font-bold">
							2
						</span>
						{t('docs.programmaticTriggers.step2.title')}
					</CardTitle>
				</CardHeader>
				<CardContent className="text-gray-700 dark:text-gray-300 space-y-3">
					<p>{t('docs.programmaticTriggers.step2.instruction')}</p>
					<CodeBlock
						code={`<script
    defer
    src="https://cdn.feedbask.com/widget.js"
    data-client-key="YOUR_WIDGET_ID"
    data-language="en"
    id="feedbask-widget-script"
></script>`}
						language="html"
						copyLabel={t('docs.programmaticTriggers.step2.copyLabel')}
						copyMessage={t('docs.programmaticTriggers.step2.copyMessage')}
					/>
				</CardContent>
			</Card>

			<Card className="mb-8 shadow-sm dark:bg-gray-800/50">
				<CardHeader>
					<CardTitle className="text-xl font-semibold flex items-center gap-2">
						<span className="flex items-center justify-center w-6 h-6 bg-primary/10 text-primary rounded-full text-sm font-bold">
							3
						</span>
						{t('docs.programmaticTriggers.step3.title')}
					</CardTitle>
				</CardHeader>
				<CardContent className="text-gray-700 dark:text-gray-300 space-y-4">
					<p>
						{t('docs.programmaticTriggers.step3.instruction')}
					</p>
					<CodeBlock
						code={`// Open the feedback widget
window.feedbask.open()`}
						language="javascript"
						copyLabel={t('docs.programmaticTriggers.step3.copyLabel')}
						copyMessage={t('docs.programmaticTriggers.step3.copyMessage')}
					/>

					<div className="space-y-4">
						<h4 className="font-semibold">
							{t('docs.programmaticTriggers.commonExamples.title')}
						</h4>

						<div className="space-y-3">
							<div className="p-3 bg-slate-50 dark:bg-slate-900/50 rounded-md">
								<h5 className="font-medium mb-2">
									{t('docs.programmaticTriggers.commonExamples.example1.title')}
								</h5>
								<CodeBlock
									code={`<button onclick="window.feedbask.open()">
  Give Feedback
</button>`}
									language="html"
									copyLabel={t('docs.programmaticTriggers.commonExamples.example1.copyLabel')}
									copyMessage={t('docs.programmaticTriggers.commonExamples.example1.copyMessage')}
								/>
							</div>

							<div className="p-3 bg-slate-50 dark:bg-slate-900/50 rounded-md">
								<h5 className="font-medium mb-2">
									{t('docs.programmaticTriggers.commonExamples.example2.title')}
								</h5>
								<CodeBlock
									code={`// After a successful purchase
function handlePurchaseSuccess() {
  showSuccessMessage()
  
  // Show feedback widget after 2 seconds
  setTimeout(() => {
    window.feedbask.open()
  }, 2000)
}`}
									language="javascript"
									copyLabel={t('docs.programmaticTriggers.commonExamples.example2.copyLabel')}
									copyMessage={t('docs.programmaticTriggers.commonExamples.example2.copyMessage')}
								/>
							</div>

							<div className="p-3 bg-slate-50 dark:bg-slate-900/50 rounded-md">
								<h5 className="font-medium mb-2">
									{t('docs.programmaticTriggers.commonExamples.example3.title')}
								</h5>
								<CodeBlock
									code={`<nav>
  <a href="/">Home</a>
  <a href="/about">About</a>
  <a href="/contact">Contact</a>
  <a href="#" onclick="window.feedbask.open(); return false;">
    Feedback
  </a>
</nav>`}
									language="html"
									copyLabel={t('docs.programmaticTriggers.commonExamples.example3.copyLabel')}
									copyMessage={t('docs.programmaticTriggers.commonExamples.example3.copyMessage')}
								/>
							</div>
						</div>
					</div>
				</CardContent>
			</Card>

			<Card className="shadow-sm dark:bg-gray-800/50">
				<CardHeader>
					<CardTitle className="text-xl font-semibold">
						{t('docs.programmaticTriggers.bestPractices.title')}
					</CardTitle>
					<CardDescription className="dark:text-gray-400">
						{t('docs.programmaticTriggers.bestPractices.description')}
					</CardDescription>
				</CardHeader>
				<CardContent className="text-gray-700 dark:text-gray-300">
					<div className="space-y-3">
						<div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-md border border-green-200 dark:border-green-700">
							<h5 className="font-medium text-green-800 dark:text-green-200 mb-2">
								✅ {t('docs.programmaticTriggers.bestPracticesList.title')}
							</h5>
							<ul className="text-sm text-green-700 dark:text-green-300 space-y-1">
								<li>
									• <strong>{t('docs.programmaticTriggers.bestPracticesList.item1.label')}</strong>{' '}
									{t('docs.programmaticTriggers.bestPracticesList.item1.text')}
								</li>
								<li>
									• <strong>{t('docs.programmaticTriggers.bestPracticesList.item2.label')}</strong>{' '}
									{t('docs.programmaticTriggers.bestPracticesList.item2.text')}
								</li>
								<li>
									• <strong>{t('docs.programmaticTriggers.bestPracticesList.item3.label')}</strong>{' '}
									{t('docs.programmaticTriggers.bestPracticesList.item3.text')}
								</li>
								<li>
									• <strong>{t('docs.programmaticTriggers.bestPracticesList.item4.label')}</strong>{' '}
									{t('docs.programmaticTriggers.bestPracticesList.item4.text')}
								</li>
								<li>
									• <strong>{t('docs.programmaticTriggers.bestPracticesList.item5.label')}</strong>{' '}
									{t('docs.programmaticTriggers.bestPracticesList.item5.text')}
								</li>
								<li>
									• <strong>{t('docs.programmaticTriggers.bestPracticesList.item6.label')}</strong>{' '}
									{t('docs.programmaticTriggers.bestPracticesList.item6.text')}
								</li>
							</ul>
						</div>

						<div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-md border border-red-200 dark:border-red-700">
							<h5 className="font-medium text-red-800 dark:text-red-200 mb-2">
								❌ {t('docs.programmaticTriggers.commonMistakes.title')}
							</h5>
							<ul className="text-sm text-red-700 dark:text-red-300 space-y-1">
								<li>
									• <strong>{t('docs.programmaticTriggers.commonMistakes.item1.label')}</strong>{' '}
									{t('docs.programmaticTriggers.commonMistakes.item1.text')}
								</li>
								<li>
									• <strong>{t('docs.programmaticTriggers.commonMistakes.item2.label')}</strong>{' '}
									{t('docs.programmaticTriggers.commonMistakes.item2.text')}
								</li>
								<li>
									• <strong>{t('docs.programmaticTriggers.commonMistakes.item3.label')}</strong>{' '}
									{t('docs.programmaticTriggers.commonMistakes.item3.text')}
								</li>
								<li>
									• <strong>{t('docs.programmaticTriggers.commonMistakes.item4.label')}</strong>{' '}
									{t('docs.programmaticTriggers.commonMistakes.item4.text')}
								</li>
								<li>
									• <strong>{t('docs.programmaticTriggers.commonMistakes.item5.label')}</strong>{' '}
									{t('docs.programmaticTriggers.commonMistakes.item5.text')}
								</li>
								<li>
									• <strong>{t('docs.programmaticTriggers.commonMistakes.item6.label')}</strong>{' '}
									{t('docs.programmaticTriggers.commonMistakes.item6.text')}
								</li>
							</ul>
						</div>
					</div>
				</CardContent>
			</Card>
		</section>
	)
}